import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Field, Form, reduxForm } from "redux-form";
import { connect } from "react-redux";
import moment from "moment";
import DatePicker from "../../components/formComponent/DatePicker/DatePicker";
import FilterSelect from "../../components/formComponent/FilterSelect/FilterSelect";
import Button from "../../components/ui/Button/Button";
import close from "../../../style/icons/Close/close-icon.svg";
import calendar from "../../../style/icons/Calendar/calendar.svg";
import downloadIcon from "../../../style/icons/Download/download.svg";
import searchIcon from "../../../style/icons/Search/search-icon.svg";
import { getAllSpecializations } from "../../../redux/specializations/actions";
import { getAllCompanies } from "../../../redux/сompanies/actions";
import { getInsuranceCompanies, getFilteredInsuranceCompanies } from "../../../redux/employees/actions";
import * as employeeTypes from "../../../redux/employees/types";
import { setInitalValues } from "../../../redux/formInitialValues/actions";
import "./ConsultationsReport.scss";
import debounce from "lodash/debounce";

const { REACT_APP_TEST_HOST } = process.env;

const ConsultationsReport = ({ 
  specialties = [], 
  companies = [],
  insuranceCompanies = [],
  handleSubmit, 
  formValues = {},
  setInitalValues,
  reset,
  change,
  getAllSpecializations,
  getAllCompanies,
  getInsuranceCompanies,
  getFilteredInsuranceCompanies,
  dispatch
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDoctorsDropdown, setShowDoctorsDropdown] = useState(false);
  const [searchingDoctors, setSearchingDoctors] = useState(false);
  const [exportMode, setExportMode] = useState('sync'); // 'sync' or 'async'
  const [statuses] = useState([
    { value: "new", title: t("newConsultation") },
    { value: "filled", title: t("filledConsultation") },
    { value: "closed", title: t("closedConsultation") },
    { value: "in_process", title: t("inProgressConsultation") },
    { value: "cancelled", title: t("cancelledConsultation") }
  ]);

  const [paymentTypes] = useState([
    { value: "subscription", title: t("subscriptionPayment"), id: "subscription" },
    { value: "oneTime", title: t("oneTimePayment"), id: "oneTime" }
  ]);

  useEffect(() => {
    getAllSpecializations(50);
    getAllCompanies(1);
    getInsuranceCompanies();
  }, [getAllSpecializations, getAllCompanies, getInsuranceCompanies]);
  
  // Create memoized search debounce function
  const debouncedSearch = useCallback(
    debounce((query, specialtyIds) => {
      searchDoctors(query, specialtyIds);
    }, 300),
    []
  );
  
  const searchDoctors = async (query, specialtyIds = []) => {
    setSearchingDoctors(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      
      specialtyIds.forEach(id => {
        params.append('specialtyId', id);
      });
      
      const response = await fetch(
        `${REACT_APP_TEST_HOST}/api/admin/search/doctors-by-specialty?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );
      
      if (response.ok) {
        const responseData = await response.json();
        
        // Check if the response follows the new format or the old format
        if (responseData.data && Array.isArray(responseData.data)) {
          // New format: direct array of doctors in the data property
          const formattedDoctors = responseData.data.map(item => ({
            id: item.id, // Doctor ID for export
            fullName: item.fullName,
            specialtyTitle: item.specialtyTitle || ""
          }));
          setDoctors(formattedDoctors);
        } else if (responseData.data && responseData.data.items) {
          // Old format: nested items array with employee data
          const doctorItems = responseData.data.items || [];
          const formattedDoctors = doctorItems.map(item => ({
            id: item.id, // We'll use the doctor's ID
            fullName: item.employee.fullName,
            specialtyTitle: item.specialty.map(s => s.title).join(', '),
            employeeId: item.employee.id,
            photoUrl: item.employee.photoUrl
          }));
          setDoctors(formattedDoctors);
        } else {
          console.error('Unexpected doctor search response format');
          setDoctors([]);
        }
      } else {
        console.error('Doctor search failed');
        setDoctors([]);
      }
    } catch (error) {
      console.error('Doctor search error:', error);
      setDoctors([]);
    } finally {
      setSearchingDoctors(false);
    }
  };

  // Handle doctor search input changes
  const handleDoctorSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowDoctorsDropdown(true);
    
    // Get selected specialty IDs
    const specialtyIds = formValues.specialtyId && Array.isArray(formValues.specialtyId)
      ? formValues.specialtyId.map(spec => spec.id)
      : formValues.specialtyId?.id 
        ? [formValues.specialtyId.id]
        : [];
    
    debouncedSearch(query, specialtyIds);
  };
  
  // Handle specialty changes to trigger doctor search update
  useEffect(() => {
    if (showDoctorsDropdown && formValues.specialtyId) {
      const specialtyIds = Array.isArray(formValues.specialtyId)
        ? formValues.specialtyId.map(spec => spec.id)
        : [formValues.specialtyId.id];
      
      debouncedSearch(searchQuery, specialtyIds);
    }
  }, [formValues.specialtyId, debouncedSearch]);

  // Function to fetch insurance companies for multiple companies and combine results
  const fetchInsuranceCompaniesForMultipleCompanies = async (companyIds) => {
    try {
      // Fetch insurance companies for each selected company
      const promises = companyIds.map(async (companyId) => {
        const response = await fetch(`${REACT_APP_TEST_HOST}/api/admin/companies/${companyId}/insurance-companies`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.items || [];
        }
        return [];
      });

      const results = await Promise.all(promises);
      
      // Combine and deduplicate insurance companies
      const combinedInsuranceCompanies = [];
      const seenIds = new Set();
      
      results.forEach(insuranceCompaniesArray => {
        insuranceCompaniesArray.forEach(company => {
          if (!seenIds.has(company.id)) {
            seenIds.add(company.id);
            combinedInsuranceCompanies.push(company);
          }
        });
      });

      // Manually dispatch the combined results to Redux store
      // Since we're bypassing the normal action, we need to dispatch directly
      // The reducer expects data.data.items structure
      dispatch({
        type: employeeTypes.GET_INSURANCE_COMPANIES,
        data: { data: { items: combinedInsuranceCompanies } }
      });
      
      return combinedInsuranceCompanies;
    } catch (error) {
      console.error('Error fetching insurance companies for multiple companies:', error);
      return [];
    }
  };

  // Handle company selection changes to filter insurance companies
  useEffect(() => {
    const selectedCompanies = formValues.companyId;
    
    if (selectedCompanies && selectedCompanies.length > 0) {
      // When companies are selected, load filtered insurance companies
      if (Array.isArray(selectedCompanies)) {
        const companyIds = selectedCompanies.map(company => company.id).filter(Boolean);
        if (companyIds.length > 0) {
          fetchInsuranceCompaniesForMultipleCompanies(companyIds);
        }
      } else {
        const companyId = selectedCompanies?.id;
        if (companyId) {
          getFilteredInsuranceCompanies(companyId);
        }
      }
    } else {
      // No companies selected, show all insurance companies
      getInsuranceCompanies();
    }
  }, [formValues.companyId]);

  // Handle insurance company changes to filter companies
  const handleInsuranceCompanyChange = (selectedInsuranceCompanies) => {
    // Clear selected companies when insurance company changes
    if (formValues.companyId) {
      setInitalValues({
        ...formValues,
        companyId: null
      });
    }
  };

  // Handle company changes to filter insurance companies
  const handleCompanyChange = async (selectedCompanies) => {
    // Clear selected insurance companies when company changes
    if (formValues.insuranceCompanyId) {
      setInitalValues({
        ...formValues,
        insuranceCompanyId: null
      });
    }

    // Load filtered insurance companies based on selected companies
    if (selectedCompanies && selectedCompanies.length > 0) {
      if (Array.isArray(selectedCompanies)) {
        // If multiple companies are selected, load insurance companies for all of them
        const companyIds = selectedCompanies.map(company => company.id).filter(Boolean);
        if (companyIds.length > 0) {
          fetchInsuranceCompaniesForMultipleCompanies(companyIds);
        }
      } else {
        // Single company selected
        const companyId = selectedCompanies?.id;
        if (companyId) {
          getFilteredInsuranceCompanies(companyId);
        }
      }
    } else {
      // If no companies selected, load all insurance companies
      getInsuranceCompanies();
    }
  };

  // Toggle selected doctor
  const toggleDoctor = (doctor) => {
    const isSelected = selectedDoctors.some(d => d.id === doctor.id);
    
    if (isSelected) {
      setSelectedDoctors(selectedDoctors.filter(d => d.id !== doctor.id));
    } else {
      setSelectedDoctors([...selectedDoctors, doctor]);
    }
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return null;
    const parsedDate = moment(dateStr, "DD/MM/YYYY", true);
    return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : null;
  };

  const handleExport = async (values) => {
    if (exportMode === 'async') {
      await handleAsyncExport(values);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (values.dateFrom) params.append('dateFrom', formatDateForAPI(values.dateFrom));
      if (values.dateTo) params.append('dateTo', formatDateForAPI(values.dateTo));
      
      // Use doctorId parameters for selected doctors
      if (selectedDoctors && selectedDoctors.length > 0) {
        selectedDoctors.forEach(doctor => {
          params.append('doctorId[]', doctor.id);
        });
      }
      
      // Handle multiple specialties
      if (values.specialtyId && Array.isArray(values.specialtyId)) {
        values.specialtyId.forEach(specialty => {
          params.append('specialtyId[]', specialty.id);
        });
      } else if (values.specialtyId) {
        params.append('specialtyId[]', values.specialtyId.id);
      }
      
      // Handle multiple statuses
      if (values.status && Array.isArray(values.status)) {
        values.status.forEach(status => {
          if (status.value) {
            params.append('status[]', status.value);
          }
        });
      } else if (values.status && values.status.value) {
        params.append('status[]', values.status.value);
      }

      // Handle insurance company filter
      if (values.insuranceCompanyId && Array.isArray(values.insuranceCompanyId)) {
        values.insuranceCompanyId.forEach(insuranceCompany => {
          params.append('insuranceCompanyId[]', insuranceCompany.id);
        });
      } else if (values.insuranceCompanyId) {
        params.append('insuranceCompanyId[]', values.insuranceCompanyId.id);
      }

      // Handle company filter
      if (values.companyId && Array.isArray(values.companyId)) {
        values.companyId.forEach(company => {
          params.append('companyId[]', company.id);
        });
      } else if (values.companyId) {
        params.append('companyId[]', values.companyId.id);
      }

      // Handle payment type filter
      if (values.paymentType && Array.isArray(values.paymentType)) {
        values.paymentType.forEach(paymentType => {
          if (paymentType.value) {
            params.append('paymentType[]', paymentType.value);
          }
        });
      } else if (values.paymentType && values.paymentType.value) {
        params.append('paymentType[]', values.paymentType.value);
      }

      console.log('Export params:', params.toString());

      const response = await fetch(`${REACT_APP_TEST_HOST}/api/admin/consultations/export?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'consultations-report.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAsyncExport = async (values) => {
    if (!values.email) {
      alert('Please enter your email address for async export');
      return;
    }

    setAsyncLoading(true);
    try {
      const formData = new FormData();
      
      if (values.dateFrom) formData.append('dateFrom', formatDateForAPI(values.dateFrom));
      if (values.dateTo) formData.append('dateTo', formatDateForAPI(values.dateTo));
      
      // Use doctorId parameters for selected doctors
      if (selectedDoctors && selectedDoctors.length > 0) {
        selectedDoctors.forEach(doctor => {
          formData.append('doctorId[]', doctor.id);
        });
      }
      
      // Handle multiple specialties
      if (values.specialtyId && Array.isArray(values.specialtyId)) {
        values.specialtyId.forEach(specialty => {
          formData.append('specialtyId[]', specialty.id);
        });
      } else if (values.specialtyId) {
        formData.append('specialtyId[]', values.specialtyId.id);
      }
      
      // Handle multiple statuses
      if (values.status && Array.isArray(values.status)) {
        values.status.forEach(statusItem => {
          if (statusItem.value) {
            formData.append('status[]', statusItem.value);
          }
        });
      } else if (values.status && values.status.value) {
        formData.append('status[]', values.status.value);
      }

      // Handle insurance company filter
      if (values.insuranceCompanyId && Array.isArray(values.insuranceCompanyId)) {
        values.insuranceCompanyId.forEach(insuranceCompany => {
          formData.append('insuranceCompanyId[]', insuranceCompany.id);
        });
      } else if (values.insuranceCompanyId) {
        formData.append('insuranceCompanyId[]', values.insuranceCompanyId.id);
      }

      // Handle company filter
      if (values.companyId && Array.isArray(values.companyId)) {
        values.companyId.forEach(company => {
          formData.append('companyId[]', company.id);
        });
      } else if (values.companyId) {
        formData.append('companyId[]', values.companyId.id);
      }

      // Handle payment type filter
      if (values.paymentType && Array.isArray(values.paymentType)) {
        values.paymentType.forEach(paymentType => {
          if (paymentType.value) {
            formData.append('paymentType[]', paymentType.value);
          }
        });
      } else if (values.paymentType && values.paymentType.value) {
        formData.append('paymentType[]', values.paymentType.value);
      }

      formData.append('email', values.email);

      const response = await fetch(`${REACT_APP_TEST_HOST}/api/admin/consultations/export/async`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData
      });

      if (!response.ok) throw new Error('Async export failed');

      const result = await response.json();
      
      if (result.success) {
        alert('Export scheduled successfully! You will receive the report via email.');
      } else {
        alert('Export failed: ' + result.message);
      }
    } catch (error) {
      console.error('Async export failed:', error);
      alert('Export failed: ' + error.message);
    } finally {
      setAsyncLoading(false);
    }
  };

  const handleClearDate = async () => {
    // Use redux-form's change method to clear the date fields
    await change("dateFrom", null);
    await change("dateTo", null);
  };

  const handleClearAll = () => {
    setSelectedDoctors([]);
    setSearchQuery("");
    setDoctors([]);
    reset();
    setInitalValues({});
  };

  const handleClearDoctors = () => {
    setSelectedDoctors([]);
    setSearchQuery("");
  };

  // Clear only search query, keep selected doctors
  const handleClearSearchQuery = () => {
    setSearchQuery("");
    setShowDoctorsDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDoctorsDropdown(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Use useMemo to recreate the list when language changes or specialties update
  const specialtiesOptions = useMemo(() => 
    specialties.map(s => ({ 
      id: s.id, 
      title: s.title,
      value: s.id
    }))
  , [specialties, i18n.language, t]);

  // Use useMemo for companies options
  const companiesOptions = useMemo(() => 
    companies.map(c => ({ 
      id: c.id, 
      title: c.title,
      value: c.id
    }))
  , [companies, i18n.language, t]);

  // Use useMemo for insurance companies options
  const insuranceCompaniesOptions = useMemo(() => 
    insuranceCompanies.map(ic => ({ 
      id: ic.id, 
      title: ic.title,
      value: ic.id
    }))
  , [insuranceCompanies, i18n.language, t]);

  return (
    <div className="consultationsReportContainer flex justifyBeetwen">
      <div className="mainContent">
        <div className="title">{t("consultationsReport")}</div>
      </div>
      
      <div className="wrappFilterForm">
        <div className="formTitle">
          {t("filterFormTitle")}
          <div className="resetButtonBox" onClick={handleClearAll}>
            <span className="resetText">{t("reset")}</span>
            <img
              className="resetForm"
              src={close}
              alt="close"
            />
          </div>
        </div>
        
        <Form className="monitoringFilters" onSubmit={handleSubmit(handleExport)}>
          <div className="formContent">
            <div className="dateRange">
              <Field
                name="dateFrom"
                component={DatePicker}
                placeholder={t("consultationDateFrom")}
                maxDate={2030}
              />
              <Field
                name="dateTo"
                component={DatePicker}
                placeholder={t("consultationDateTo")}
                maxDate={2030}
                isValidDate={(current) => {
                  const dateFrom = formValues.dateFrom;
                  if (!dateFrom) return true;
                  const fromDate = moment(dateFrom, "DD/MM/YYYY");
                  return current.isSameOrAfter(fromDate, 'day');
                }}
              />
              <img
                src={close}
                alt="close"
                onClick={handleClearDate}
              />
              <img src={calendar} alt="calendar" />
            </div>

            <div className="formLabel">{t("doctorSpecialty")}</div>
            <Field
              name="specialtyId"
              component={FilterSelect}
              placeholder={t("doctorSpecialty")}
              options={specialtiesOptions}
              withPreview
              multiple
              selectAll={true}
            />

            <div className="formLabel">{t("doctorFullName")}</div>
            <div className="doctorSearch" onClick={(e) => e.stopPropagation()}>
              <div className="doctorSearchInput">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleDoctorSearchChange}
                  placeholder={t("enterDoctorNames")}
                  onFocus={() => setShowDoctorsDropdown(true)}
                />
                <img src={searchIcon} alt="search" className="searchIcon" />
                {searchQuery && (
                  <button type="button" className="clearButton" onClick={handleClearSearchQuery}>
                    <img src={close} alt="clear" />
                  </button>
                )}
              </div>
              
              {selectedDoctors.length > 0 && (
                <div className="selectedDoctors">
                  <div className="selectedDoctorsHeader">
                    <span className="selectedDoctorsTitle">{t("selectedDoctors")}</span>
                    <button 
                      type="button" 
                      onClick={handleClearDoctors}
                      className="clearAllDoctorsButton"
                      title={t("clearAllDoctors")}
                    >
                      {t("clearAll")}
                    </button>
                  </div>
                  {selectedDoctors.map((doctor) => (
                    <div key={doctor.id} className="selectedDoctor">
                      {doctor.fullName}
                      <button 
                        type="button" 
                        onClick={() => toggleDoctor(doctor)}
                        className="removeDoctor"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {showDoctorsDropdown && (
                <div className="doctorsDropdown">
                  {searchingDoctors ? (
                    <div className="dropdownMessage">{t("loading")}</div>
                  ) : doctors.length > 0 ? (
                    doctors.map((doctor) => (
                      <div 
                        key={doctor.id} 
                        className={`doctorOption ${selectedDoctors.some(d => d.id === doctor.id) ? 'selected' : ''}`}
                        onClick={() => toggleDoctor(doctor)}
                      >
                        <div className="doctorInfo">
                          {doctor.photoUrl && (
                            <img src={doctor.photoUrl} alt={doctor.fullName} className="doctorPhoto" />
                          )}
                          <div className="doctorDetails">
                            <div className="doctorName">{doctor.fullName}</div>
                            <div className="doctorSpecialty">{doctor.specialtyTitle}</div>
                          </div>
                        </div>
                        {selectedDoctors.some(d => d.id === doctor.id) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="dropdownMessage">{t("noResults")}</div>
                  )}
                </div>
              )}
            </div>

            <div className="formLabel">{t("company")}</div>
            <Field
              name="companyId"
              component={FilterSelect}
              placeholder={t("company")}
              options={companiesOptions}
              withPreview
              multiple
              selectAll={true}
              onChange={handleCompanyChange}
            />

            <div className="formLabel">{t("insuranceCompany")}</div>
            <Field
              name="insuranceCompanyId"
              component={FilterSelect}
              placeholder={t("insuranceCompany")}
              options={insuranceCompaniesOptions}
              withPreview
              multiple
              selectAll={true}
              onChange={handleInsuranceCompanyChange}
            />

            <div className="formLabel">{t("status")}</div>
            <Field
              name="status"
              component={FilterSelect}
              placeholder={t("status")}
              options={statuses}
              withPreview
              multiple
              selectAll={true}
            />

            <div className="formLabel">{t("paymentType")}</div>
            <Field
              name="paymentType"
              component={FilterSelect}
              placeholder={t("paymentType")}
              options={paymentTypes}
              withPreview
              multiple
              selectAll={true}
            />

            <div className="formLabel">{t("exportMode")}</div>
            <div className="exportModeSelector">
              <label className="exportModeOption">
                <input
                  type="radio"
                  name="exportMode"
                  value="sync"
                  checked={exportMode === 'sync'}
                  onChange={(e) => setExportMode(e.target.value)}
                />
                <span>{t("instantExport")}</span>
              </label>
              <label className="exportModeOption">
                <input
                  type="radio"
                  name="exportMode"
                  value="async"
                  checked={exportMode === 'async'}
                  onChange={(e) => setExportMode(e.target.value)}
                />
                <span>{t("asyncExport")}</span>
              </label>
            </div>

            {exportMode === 'async' && (
              <div className="emailField">
                <div className="formLabel">{t("emailForReport")}</div>
                <Field
                  name="email"
                  component="input"
                  type="email"
                  placeholder={t("enterYourEmail")}
                  className="form-control"
                  required
                />
              </div>
            )}

            <div className="formFooter">
              <Button
                type="submit"
                text={exportMode === 'async' ? t("scheduleExport") : t("downloadReport")}
                icon={downloadIcon}
                loading={exportMode === 'async' ? asyncLoading : loading}
                withIcon
                className="btn save"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  specialties: state.specialization?.items || [],
  companies: state.companies?.items || [],
  insuranceCompanies: state.employees?.insuranceCompanies || [],
  formValues: state.form?.consultationsReport?.values || {},
});

const mapDispatchToProps = {
  getAllSpecializations,
  getAllCompanies,
  getInsuranceCompanies,
  getFilteredInsuranceCompanies,
  setInitalValues
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: "consultationsReport",
    enableReinitialize: true
  })(ConsultationsReport)
); 